import {
  builtInViewLabel,
  chromeLabelText,
  type ChromeTranslateFn,
} from '@featit/shared/chrome-label';
import { type ISavedView } from '@featit/shared/types/index';
import {
  GENERIC_VIEW_SEED_IDS,
  listGenericViewSeedHeadings,
} from '@featit/shared/view-engine/generic-view-seed-headings';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import type { TFn } from '@/i18n/LocaleProvider';

/**
 * T-0591: what names a view, apart from what defines it. `viewCatalog.ts` builds the definitions,
 * which is the whole view engine; the sidebar's rows, the page title and the address need only the
 * names and ids, and the web entry imports them from here so it carries none of the engine.
 */

export const DEFAULT_VIEW_ID_PREFIX = 'default-view:';

/**
 * The catalog id as it travels in a URL: a seed by its bare seed id, a saved view by its
 * own id. Keeps `/acme/area/-/views/roadmap` readable instead of `default-view%3Aroadmap`.
 */
export function viewUrlId(configurationId: string): string {
  return configurationId.startsWith(DEFAULT_VIEW_ID_PREFIX)
    ? configurationId.slice(DEFAULT_VIEW_ID_PREFIX.length)
    : configurationId;
}

/**
 * I-1366 (IL-18a): the name of the view a URL segment names — a built-in by its product name, a
 * saved view by its own — or null while nothing answers to it (a saved view before its work area's
 * views have loaded). The phone's back row and the app's screen title are called by it.
 */
export function viewNameOfUrlId(
  savedViews: ISavedView[],
  urlId: string | null | undefined,
  t: ChromeTranslateFn,
): string | null {
  if (!urlId) return null;
  const builtIn = listGenericViewSeedHeadings().find((heading) => heading.id === urlId);
  if (builtIn)
    return viewNameIn(t, { id: `${DEFAULT_VIEW_ID_PREFIX}${builtIn.id}`, name: builtIn.name });
  /*
    T-0591: the name needs no definition, and building the catalog to read one put the whole view
    engine in the web entry. A folder and a built-in's placement row stand in the list but are no
    view to open, as they were not in the catalog.
  */
  const saved = savedViews.find((view) => view.id === urlId && (view.kind ?? 'view') === 'view');
  return saved?.name ?? null;
}

/**
 * T-0846 (IN-34, IN-35): the name a view is drawn under in the reader's language. A built-in (its
 * catalog id `default-view:<seed>`, or a placement row's `seedId`) is the product's, so it is said
 * through `t`; a saved view or a folder is the person's, and its own name is drawn as it is, even
 * when it reads "Roadmap".
 */
export function viewNameIn(
  t: ChromeTranslateFn,
  view: Pick<ISavedView, 'id' | 'name'> & Partial<Pick<ISavedView, 'kind' | 'seedId'>>,
): string {
  const seedId =
    view.kind === 'builtin' && view.seedId
      ? view.seedId
      : view.id.startsWith(DEFAULT_VIEW_ID_PREFIX)
        ? view.id.slice(DEFAULT_VIEW_ID_PREFIX.length)
        : null;
  const label = seedId ? builtInViewLabel(seedId) : null;
  return label ? chromeLabelText(label, t) : view.name;
}

const BUILT_IN_VIEW_IDS: ReadonlySet<string> = new Set(Object.values(GENERIC_VIEW_SEED_IDS));

/**
 * F-0621 (walkthrough A in German): a built-in view's name in the reader's language, by its seed id
 * (`navigation.builtInViews`); a saved view, and a seed this table does not know, keep `name`.
 */
export function builtInViewWords(t: TFn, configurationId: string, name: string): string {
  if (!configurationId.startsWith(DEFAULT_VIEW_ID_PREFIX)) return name;
  const seedId = configurationId.slice(DEFAULT_VIEW_ID_PREFIX.length);
  return BUILT_IN_VIEW_IDS.has(seedId)
    ? t(`navigation.builtInViews.${seedId}` as MessageKey)
    : name;
}
